import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, Shield, Truck, CreditCard, LineChart, Layers, Check, Sparkles, ChevronRight } from "lucide-react";

// Stripe / Linear inspired: deep ink navy + electric violet + crisp cyan
// Typography: Geist-like (Inter as fallback) — tight, grotesk

export default function LandingDark() {
  return (
    <div className="min-h-screen bg-[#07090D] text-[#E6E7EB] relative overflow-x-hidden" style={{ fontFamily: "Inter, ui-sans-serif, system-ui" }}>
      <style>{`
        .dark-gradient-mesh {
          background:
            radial-gradient(600px circle at 20% 10%, rgba(94, 106, 210, 0.25), transparent 50%),
            radial-gradient(500px circle at 80% 5%, rgba(0, 200, 255, 0.12), transparent 55%),
            radial-gradient(700px circle at 50% 60%, rgba(94, 106, 210, 0.08), transparent 60%);
        }
        .dark-grid {
          background-image:
            linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse 80% 50% at 50% 0%, #000 40%, transparent 100%);
        }
        .glass {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .glass-soft {
          background: linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01));
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .noise::before {
          content: '';
          position: absolute; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.4'/%3E%3C/svg%3E");
          opacity: 0.05; pointer-events: none; mix-blend-mode: overlay;
        }
        .shine {
          background: linear-gradient(135deg, #5E6AD2 0%, #8B7FFF 50%, #00D9FF 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .btn-primary {
          background: linear-gradient(180deg, #5E6AD2, #4750B8);
          box-shadow:
            0 0 0 1px rgba(255,255,255,0.1) inset,
            0 1px 0 rgba(255,255,255,0.2) inset,
            0 10px 30px -10px rgba(94, 106, 210, 0.6);
          color: white;
        }
        .btn-primary:hover { filter: brightness(1.1); }
        .btn-ghost {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.12);
          color: #E6E7EB;
        }
        .btn-ghost:hover { background: rgba(255,255,255,0.08); }
        .tiny-pill {
          display:inline-flex; align-items:center; gap:.375rem;
          padding:.25rem .625rem; border-radius:9999px;
          font-size:.7rem; font-weight:500;
          background: rgba(94, 106, 210, 0.12);
          border: 1px solid rgba(94, 106, 210, 0.3);
          color: #B5BBFF;
          letter-spacing: .02em;
        }
        .mono { font-family: ui-monospace, "JetBrains Mono", monospace; }
        .fadeup { animation: fadeup .6s ease-out both; }
        @keyframes fadeup { from { opacity:0; transform: translateY(10px); } to { opacity:1; transform: translateY(0); } }
        .hover-card:hover { border-color: rgba(94,106,210,0.4); background: rgba(94,106,210,0.05); }
      `}</style>

      {/* Nav */}
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-[#07090D]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/preview/dark" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#5E6AD2] to-[#00D9FF] grid place-items-center">
              <span className="text-white font-bold text-xs">v</span>
            </div>
            <span className="font-semibold tracking-tight">verqo</span>
            <span className="ml-2 tiny-pill mono">DARK PREVIEW</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#products">Products</a>
            <a href="#score">Score</a>
            <a href="#pricing">Pricing</a>
            <a href="#docs">Docs</a>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="text-white/60 hover:text-white">← Light version</Link>
            <Link to="/login" className="text-white/80 hover:text-white">Sign in</Link>
            <Link to="/register">
              <button className="btn-primary rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-1">Get started <ArrowUpRight className="w-3.5 h-3.5"/></button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative noise">
        <div className="absolute inset-0 dark-gradient-mesh"/>
        <div className="absolute inset-0 dark-grid"/>
        <div className="max-w-7xl mx-auto px-6 pt-28 pb-24 relative">
          <div className="tiny-pill fadeup">
            <Sparkles className="w-3 h-3"/> Infrastructure · v1.0 · now in early access
          </div>
          <h1 className="mt-6 text-[clamp(2.75rem,6vw,5.5rem)] leading-[1.02] tracking-[-0.04em] font-semibold max-w-5xl fadeup" style={{animationDelay:'100ms'}}>
            Financial infrastructure<br/>
            for <span className="shine">B2B wholesale.</span>
          </h1>
          <p className="mt-6 text-lg text-white/60 max-w-2xl fadeup" style={{animationDelay:'200ms'}}>
            verqo is a single platform that moves commerce, credit, capital, insurance, and logistics for the world's wholesalers. Built like Stripe. Powered by real transaction data.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 fadeup" style={{animationDelay:'300ms'}}>
            <Link to="/register"><button className="btn-primary h-11 rounded-lg px-5 font-medium inline-flex items-center gap-1.5">Start building <ArrowUpRight className="w-4 h-4"/></button></Link>
            <button className="btn-ghost h-11 rounded-lg px-5 font-medium inline-flex items-center gap-1.5">Read the docs <ChevronRight className="w-4 h-4"/></button>
          </div>

          {/* Code-ish API preview card */}
          <div className="mt-20 grid lg:grid-cols-5 gap-5 fadeup" style={{animationDelay:'400ms'}}>
            <div className="lg:col-span-3 glass rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-white/[0.02]">
                <div className="flex items-center gap-2 text-xs mono text-white/40">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]/60"/>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]/60"/>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]/60"/>
                  <span className="ml-2">POST /v1/credit/underwrite</span>
                </div>
                <span className="tiny-pill mono">200 OK · 142ms</span>
              </div>
              <pre className="p-6 text-[13px] mono leading-relaxed text-white/80 overflow-x-auto">
<span className="text-[#8B7FFF]">const</span> <span className="text-[#00D9FF]">decision</span> <span className="text-white/40">=</span> <span className="text-[#8B7FFF]">await</span> verqo.credit.underwrite({'{'}
  buyer_id: <span className="text-[#9EFF96]">"buy_29a4F"</span>,
  amount: <span className="text-[#F8C471]">12_000_00</span>,
  terms: <span className="text-[#9EFF96]">"net60"</span>,
{'}'});
<span className="text-white/30">{'//'} {'=>'} approved · score 742 · rate 2.80% · D+1 payout</span>
<br/>
<span className="text-[#8B7FFF]">const</span> <span className="text-[#00D9FF]">policy</span> <span className="text-white/40">=</span> <span className="text-[#8B7FFF]">await</span> verqo.insurance.bind(decision.invoice_id);
<span className="text-white/30">{'//'} premium 0.12% · coverage 95% · bound by Allianz Trade</span>
              </pre>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="glass-soft rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#5E6AD2]/30 blur-3xl"/>
                <div className="text-xs uppercase tracking-widest text-white/40 relative">Verqo Trade Score</div>
                <div className="relative mt-2 flex items-end gap-3">
                  <div className="text-6xl font-semibold tracking-tighter">742</div>
                  <div className="mb-2 tiny-pill mono">↑ +6</div>
                </div>
                <div className="relative mt-4 h-1 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#5E6AD2] to-[#00D9FF]" style={{width:'82%'}}/>
                </div>
              </div>
              <div className="glass-soft rounded-2xl p-5">
                <div className="text-xs uppercase tracking-widest text-white/40">Payout · D+1</div>
                <div className="mt-2 text-3xl font-semibold">$11,904<span className="text-white/30 text-base">.00</span></div>
                <div className="mt-1 text-xs text-white/50">Factored from INV-2891 · 3.0% fee</div>
              </div>
              <div className="glass-soft rounded-2xl p-5">
                <div className="text-xs uppercase tracking-widest text-white/40">Credit Line</div>
                <div className="mt-2 text-3xl font-semibold">$45,000</div>
                <div className="mt-1 text-xs text-white/50">Net-60 · active</div>
              </div>
            </div>
          </div>

          {/* Trusted by strip */}
          <div className="mt-20 pt-10 border-t border-white/5 flex items-center justify-between gap-6 flex-wrap">
            <span className="text-xs mono uppercase tracking-widest text-white/40">Trusted by operators in</span>
            <div className="flex flex-wrap gap-6 text-sm text-white/50">
              {["🇺🇸 United States","🇬🇧 United Kingdom","🇦🇪 UAE","🇨🇦 Canada","🇦🇺 Australia","🇸🇬 Singapore"].map(c => <span key={c}>{c}</span>)}
            </div>
          </div>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="relative py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <span className="tiny-pill">The platform</span>
              <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-[-0.035em] max-w-2xl">
                One API. <span className="text-white/40">Five products.</span> Every wholesale transaction.
              </h2>
            </div>
            <p className="text-white/50 max-w-md">Each product is usable standalone. Composed together they become the wholesale operating system.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { i: Layers, t: "Commerce OS", d: "White-label portals, catalog, RFQ, orders, invoicing.", code: "verqo.orders.create()" },
              { i: LineChart, t: "Score", d: "Real-time credit intelligence. 50+ signals per decision.", code: "verqo.score.get(buyer)" },
              { i: CreditCard, t: "Capital", d: "Net-30/60/90, virtual cards, D+1 seller payouts.", code: "verqo.capital.advance()" },
              { i: Truck, t: "Logistics", d: "Multi-carrier rate engine, tracking, freight intel.", code: "verqo.shipping.rates()" },
              { i: Shield, t: "Insurance", d: "Per-invoice trade credit insurance from 0.10%.", code: "verqo.insurance.bind()" },
              { i: Zap, t: "Webhooks & API", d: "Event-driven. Build custom flows on top.", code: "verqo.webhooks.on()" },
            ].map((c, i) => (
              <div key={i} className="glass rounded-2xl p-6 hover-card transition-colors group">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#5E6AD2]/20 to-[#00D9FF]/10 border border-white/5 grid place-items-center">
                    <c.i className="w-5 h-5 text-[#B5BBFF]"/>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-white/30 group-hover:text-white/70 transition-colors"/>
                </div>
                <div className="mt-5 text-lg font-semibold tracking-tight">{c.t}</div>
                <p className="mt-1.5 text-sm text-white/50 leading-relaxed">{c.d}</p>
                <div className="mt-5 mono text-xs text-[#00D9FF]/80 bg-black/40 border border-white/5 rounded-lg px-3 py-2">{c.code}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Score feature */}
      <section id="score" className="relative py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="tiny-pill">Data moat</span>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-[-0.035em]">
              Credit intelligence<br/>built on <span className="shine">real wholesale data.</span>
            </h2>
            <p className="mt-5 text-white/60 text-lg max-w-lg">800,000+ data points per decision. Payment history, order velocity, industry benchmarks, and shipping signals — compounded.</p>
            <div className="mt-8 space-y-3">
              {[["Payment history","35%"],["Order frequency","25%"],["Revenue volume","20%"],["Product diversity","10%"],["Shipping reliability","10%"]].map((r,i)=>(
                <div key={i} className="flex items-center gap-4">
                  <span className="w-36 text-sm text-white/60">{r[0]}</span>
                  <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#5E6AD2] to-[#00D9FF]" style={{width: r[1]}}/>
                  </div>
                  <span className="mono text-xs text-white/50 w-10 text-right">{r[1]}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="glass rounded-3xl p-8 relative overflow-hidden">
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#5E6AD2]/20 blur-3xl"/>
            <div className="relative">
              <div className="flex items-center justify-between">
                <span className="text-xs mono uppercase tracking-widest text-white/40">Model response</span>
                <span className="tiny-pill mono">live</span>
              </div>
              <div className="mt-4 flex items-end gap-5">
                <div className="text-7xl font-semibold tracking-tighter">742</div>
                <div className="mb-3">
                  <div className="tiny-pill">Excellent</div>
                  <div className="mt-2 text-xs text-white/50">↑ +6 this month</div>
                </div>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-3">
                {[["Credit limit","$45,000"],["Rate","2.80%"],["TCI premium","0.12%"],["Default prob.","0.4%"]].map((s,i)=>(
                  <div key={i} className="border border-white/5 rounded-xl p-4 bg-black/20">
                    <div className="text-[10px] mono uppercase tracking-widest text-white/40">{s[0]}</div>
                    <div className="mt-1 text-xl font-semibold">{s[1]}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[["$10T","Global B2B wholesale"],["30s","Credit decision"],["0.8%","Default rate"],["D+1","Seller payout"]].map((s,i)=>(
            <div key={i}>
              <div className="text-5xl font-semibold tracking-tighter shine">{s[0]}</div>
              <div className="text-white/50 text-sm mt-2">{s[1]}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="tiny-pill">Pricing</span>
            <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-[-0.035em]">Transparent. Composable.</h2>
            <p className="mt-4 text-white/50">Subscribe to OS. Pay per transaction on Capital, Insurance and Logistics.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {t:"Starter", p:"$99", per:"/month", f:["2 active buyers included · then $30/buyer", "Full Commerce OS","1 branded domain","Owner seat only","Score included"], hl:false},
              {t:"Growth", p:"$599", per:"/month", f:["10 active buyers included · then $25/buyer", "Everything in Starter","Capital Net-30/60/90","5 team seats","D+1 payouts","Priority support"], hl:true},
              {t:"Pro", p:"$1,499", per:"/month", f:["50 active buyers included · then $20/buyer","Everything in Growth","Multi-warehouse + multi-region","20 team seats","Advanced reporting","API access"], hl:false},
              {t:"Enterprise", p:"$3,999", per:"/month", f:["100 active buyers included · then $15/buyer", "Unlimited team seats","SSO + custom contracts","Dedicated CSM","SLA"], hl:false},
            ].map((pl,i)=>(
              <div key={i} className={`rounded-2xl p-7 relative ${pl.hl ? "glass" : "glass-soft"}`}>
                {pl.hl && <>
                  <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{background:"linear-gradient(135deg, rgba(94,106,210,0.15), transparent 50%)"}}/>
                  <span className="tiny-pill relative">Most popular</span>
                </>}
                <div className="mt-3 text-lg font-semibold">{pl.t}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-5xl font-semibold tracking-tighter">{pl.p}</span>
                  <span className="text-white/40">{pl.per}</span>
                </div>
                <ul className="mt-6 space-y-3 text-sm text-white/70">
                  {pl.f.map((f,j)=>(<li key={j} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 text-[#00D9FF]/80 shrink-0"/>{f}</li>))}
                </ul>
                <Link to="/register">
                  <button className={`mt-7 w-full h-11 rounded-lg font-medium ${pl.hl?"btn-primary":"btn-ghost"}`}>Get started</button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="glass rounded-3xl p-12 md:p-16 relative overflow-hidden">
            <div className="absolute -left-20 -bottom-20 w-96 h-96 rounded-full bg-[#5E6AD2]/20 blur-3xl"/>
            <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-[#00D9FF]/10 blur-3xl"/>
            <div className="relative">
              <h2 className="text-4xl sm:text-5xl font-semibold tracking-[-0.035em] max-w-2xl">Ship wholesale infrastructure today.</h2>
              <p className="mt-5 text-white/60 max-w-xl">Start free in minutes. Add products and collect the first order on us.</p>
              <div className="mt-8 flex gap-3">
                <Link to="/register"><button className="btn-primary h-11 rounded-lg px-5 font-medium">Start free</button></Link>
                <Link to="/login"><button className="btn-ghost h-11 rounded-lg px-5 font-medium">Sign in</button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-between gap-4 text-sm text-white/40">
          <span>© 2026 verqo · Wholesale, instant.</span>
          <span className="mono">v1.0.0 · dark preview</span>
        </div>
      </footer>
    </div>
  );
}
